import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { GridPageLayout } from '@lux/grid';
import { TranslateScope } from '@pegasus/translation';
import { useConfig } from '@browsing/config';

import { hasSubscriptionEnabledCookie } from '@client/helpers/subscription-cookie';
import { SEC_SUB_SUCCESS_PLACEMENT } from '@client/config/constants';
import { getHasOrders, getEmailProvider } from '@client/redux/modules/customer/selectors';

import {
    getNewsletterSubscriptionPending,
    getNewsletterErrorCount,
} from '@client/redux/modules/newsletter/selectors';

import SecondarySubscriptionMessage from '../SecondarySubscriptionMessage';
import { useSubscriptionFlow, useHidableContainer } from './hooks';
import styles from './styles.scss';

const cn = classNames.bind(styles);

interface SecondarySubscriptionProps {
    placement?: string;
}

export const SecondarySubscription = ({ placement }: SecondarySubscriptionProps) => {
    const config = useConfig();
    const hasOrders = useSelector(getHasOrders);
    const emailProvider = useSelector(getEmailProvider);
    const pending = useSelector(getNewsletterSubscriptionPending);
    const errorCount = useSelector(getNewsletterErrorCount);
    const { salesChannel, localeCode } = config;

    const subscriptionTarget = useMemo(() => {
        return {
            saleschannel: salesChannel,
            locale: localeCode,
        };
    }, [localeCode, salesChannel]);

    const { rejected, accepted, error, subscribe, reject, confirmEmail } =
        useSubscriptionFlow(subscriptionTarget);

    const { hidden, close, isVisible } = useHidableContainer({
        error,
        accepted,
        pending,
        subscriptionTarget,
    });

    const wrapperEl = useRef<HTMLDivElement>();
    const isDoi = config.isEnabled('doi');

    if (!config.isEnabled('secondarySubscription') && !hasSubscriptionEnabledCookie()) {
        return null;
    }

    return (
        isVisible() && (
            <div
                ref={wrapperEl}
                className={cn('wrapper', {
                    hidden,
                    'success-placement': placement === SEC_SUB_SUCCESS_PLACEMENT,
                })}
            >
                <GridPageLayout>
                    <TranslateScope path="myLounge.secondarySubscription">
                        <SecondarySubscriptionMessage
                            subscribe={subscribe}
                            reject={reject}
                            close={close}
                            confirmEmail={emailProvider ? confirmEmail(emailProvider) : null}
                            rejected={rejected}
                            accepted={accepted}
                            error={Boolean(error)}
                            pending={pending}
                            hasOrders={hasOrders}
                            isCucaHelpShown={errorCount > 1}
                            isDoi={isDoi}
                            placement={placement}
                        />
                    </TranslateScope>
                </GridPageLayout>
            </div>
        )
    );
};

export default SecondarySubscription;
